/**
 * @description
 * Unified Platform Settings component combining connection and sync functionality.
 * Shows connection status at top, then sync configuration, scheduling, and history.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 13, 2025
 * @see admin/src/frontend/pages/Settings/PlatformSettings.test.tsx
 */

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../services/api";
import {
  PlatformSyncDialog,
  SyncHistoryPanel,
  SyncStatusIndicator,
  type SyncResult,
} from "../../components/PlatformSync";
import {
  PlatformSettingsProps,
  PlatformConfigState,
  PlatformConnectionStatus,
} from "./types";

/**
 * Unified platform settings component with connection and sync management
 */
export function PlatformSettings({ onRefresh }: PlatformSettingsProps) {
  const [platformConfig, setPlatformConfig] =
    useState<PlatformConfigState | null>(null);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [authToken, setAuthToken] = useState("");
  const [platformConnectionStatus, setPlatformConnectionStatus] =
    useState<PlatformConnectionStatus | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncStatusKey, setSyncStatusKey] = useState(0);

  useEffect(() => {
    fetchPlatformConfig();

    // Listen for tokens from bookmarklet or helper page
    const handleTokenMessage = async (event: MessageEvent) => {
      // Accept tokens from platform.byteable.ai or our own origin
      if (event.data?.type === "platform_token" && event.data?.token) {
        try {
          setPlatformLoading(true);
          await api.initializePlatformOAuth({ authToken: event.data.token });
          toast.success(
            "Platform OAuth initialized successfully from bookmarklet!",
          );
          await fetchPlatformConfig();
        } catch (error) {
          console.error("Error storing token from bookmarklet:", error);
          toast.error("Failed to store authentication token");
        } finally {
          setPlatformLoading(false);
        }
      }
    };

    window.addEventListener("message", handleTokenMessage);

    return () => {
      window.removeEventListener("message", handleTokenMessage);
    };
  }, []);

  const fetchPlatformConfig = async () => {
    try {
      setPlatformLoading(true);
      const response = await api.getPlatformOAuthConfig();
      setPlatformConfig(response.config);
    } catch (error) {
      console.error("Error fetching platform config:", error);
      toast.error("Failed to load platform configuration");
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleInitializePlatformOAuth = async () => {
    if (!authToken.trim()) {
      toast.error("Please enter an auth token");
      return;
    }

    try {
      setPlatformLoading(true);
      await api.initializePlatformOAuth({ authToken });
      toast.success("Platform OAuth initialized successfully");
      setAuthToken("");
      await fetchPlatformConfig();
      // Trigger sync status refresh
      setSyncStatusKey(prev => prev + 1);
      onRefresh();
    } catch (error) {
      console.error("Error initializing platform OAuth:", error);
      toast.error(
        `Failed to initialize OAuth: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setPlatformLoading(true);
      const response = await api.verifyPlatformConnection();
      setPlatformConnectionStatus(response.connection);

      if (response.connection.connected && response.connection.authenticated) {
        toast.success("Platform connection verified successfully");
        // Trigger a refresh of the sync status indicator
        setSyncStatusKey(prev => prev + 1);
        onRefresh();
      } else {
        toast.error(
          `Connection failed: ${response.connection.error || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("Error testing platform connection:", error);
      toast.error(
        `Failed to test connection: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    try {
      setPlatformLoading(true);
      const response = await api.refreshPlatformToken();

      if (response.success) {
        toast.success("Token refreshed successfully");
        await fetchPlatformConfig();
        // Trigger sync status refresh
        setSyncStatusKey(prev => prev + 1);
        onRefresh();
      } else {
        toast.error(
          `Token refresh failed: ${response.error || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      toast.error(
        `Failed to refresh token: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleDisconnectPlatform = async () => {
    if (
      !confirm(
        "Are you sure you want to disconnect from the platform? This will remove all OAuth configuration.",
      )
    ) {
      return;
    }

    try {
      setPlatformLoading(true);
      await api.disconnectPlatform();
      toast.success("Platform disconnected successfully");
      setPlatformConfig(null);
      setPlatformConnectionStatus(null);
    } catch (error) {
      console.error("Error disconnecting platform:", error);
      toast.error(
        `Failed to disconnect: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setPlatformLoading(false);
    }
  };

  const handleSyncComplete = (_result: SyncResult) => {
    toast.success("Sync completed successfully");
    onRefresh(); // Refresh parent settings
  };

  const isConnected = platformConfig?.isActive && platformConfig?.isConfigured;

  return (
    <div className="space-y-6">
      {/* Connection Status Section - Always at Top */}
      <div className="rounded-lg border p-4 bg-card">
        <h3 className="text-md font-semibold mb-3">Connection Status</h3>
        {platformLoading ? (
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : platformConfig ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div
                className={`h-2 w-2 rounded-full ${platformConfig.isActive ? "bg-green-500" : "bg-red-500"}`}
              ></div>
              <span className="text-sm font-medium">
                {platformConfig.isActive ? "Connected" : "Disconnected"}
              </span>
              {platformConfig.needsRefresh && (
                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                  Needs Refresh
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Platform: {platformConfig.platformUrl}</p>
              <p>User: {platformConfig.authenticatedUserEmail}</p>
              {platformConfig.authenticatedUserRole && (
                <p>Role: {platformConfig.authenticatedUserRole}</p>
              )}
              {platformConfig.lastVerifiedAt && (
                <p>
                  Last verified:{" "}
                  {new Date(platformConfig.lastVerifiedAt).toLocaleString()}
                </p>
              )}
              {platformConfig.tokenExpiresAt && (
                <p>
                  Token expires:{" "}
                  {new Date(platformConfig.tokenExpiresAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            No platform connection configured
          </div>
        )}
      </div>

      {/* Setup Section - Only show if not configured */}
      {!platformConfig?.isConfigured && (
        <div className="rounded-lg border p-4 bg-card border-blue-200 bg-blue-50/50">
          <div className="flex items-start gap-3 mb-4">
            <div className="rounded-full bg-blue-100 p-2">
              <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-md font-semibold text-blue-900 mb-1">
                Add Global Platform API Token
              </h3>
              <p className="text-sm text-blue-700">
                This token enables admin-level access to platform.byteable.ai for syncing clients and users across your organization.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground bg-white rounded-md p-3 border border-blue-100">
              <p className="font-medium text-foreground mb-2">What is this token for?</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Authenticate with platform.byteable.ai as an admin</li>
                <li>Sync client configurations and user data</li>
                <li>Manage platform-wide settings and permissions</li>
                <li>Enable automated synchronization schedules</li>
              </ul>
            </div>

            {/* Helper Page Button */}
            <div className="space-y-3">
              <button
                onClick={() =>
                  window.open(
                    "/platform-oauth-helper.html",
                    "_blank",
                    "width=800,height=900",
                  )
                }
                className="w-full rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 flex items-center justify-center gap-2"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Open Setup Helper
              </button>

              <div className="text-xs text-muted-foreground text-center">
                Get instructions for bookmarklet, manual extraction, or console
                script
              </div>
            </div>

            {/* Fallback Manual Method */}
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                Manual Setup (Advanced)
              </summary>
              <div className="mt-3 space-y-3 pl-4 border-l-2 border-muted">
                <div className="text-sm text-muted-foreground">
                  <p className="mb-2">
                    If the interactive method doesn't work:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 ml-4">
                    <li>
                      Navigate to{" "}
                      <a
                        href="https://platform.byteable.ai/auth/init.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        platform.byteable.ai/auth/init.html
                      </a>
                    </li>
                    <li>Click Google or GitHub to authenticate</li>
                    <li>After successful auth, open browser DevTools</li>
                    <li>
                      Go to Application tab (Chrome) or Storage tab (Firefox)
                    </li>
                    <li>Navigate to Cookies → https://platform.byteable.ai</li>
                    <li>Find the auth_token cookie and copy the token value</li>
                    <li>
                      Paste the token below and click "Initialize Connection"
                    </li>
                  </ol>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Global Platform API Token</label>
                  <p className="text-xs text-muted-foreground mb-2">
                    This is your admin authentication token from platform.byteable.ai
                  </p>
                  <input
                    type="password"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    placeholder="Paste your global platform token here..."
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    onClick={handleInitializePlatformOAuth}
                    disabled={platformLoading || !authToken.trim()}
                    className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {platformLoading
                      ? "Initializing..."
                      : "Initialize Connection"}
                  </button>
                </div>
              </div>
            </details>
          </div>
        </div>
      )}

      {/* Connection Actions - Only show if configured */}
      {platformConfig?.isConfigured && (
        <div className="rounded-lg border p-4 bg-card">
          <h3 className="text-md font-semibold mb-3">Connection Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleTestConnection}
              disabled={platformLoading}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
            >
              {platformLoading ? "Testing..." : "Test Connection"}
            </button>
            {platformConfig.needsRefresh && (
              <button
                onClick={handleRefreshToken}
                disabled={platformLoading}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
              >
                {platformLoading ? "Refreshing..." : "Refresh Token"}
              </button>
            )}
            <button
              onClick={handleDisconnectPlatform}
              disabled={platformLoading}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >
              {platformLoading ? "Disconnecting..." : "Disconnect"}
            </button>
          </div>
        </div>
      )}

      {/* Connection Test Results */}
      {platformConnectionStatus && (
        <div className="rounded-lg border p-4 bg-card">
          <h3 className="text-md font-semibold mb-3">
            Connection Test Results
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div
                className={`h-2 w-2 rounded-full ${platformConnectionStatus.connected ? "bg-green-500" : "bg-red-500"}`}
              ></div>
              <span className="text-sm font-medium">
                {platformConnectionStatus.connected
                  ? "Connected"
                  : "Disconnected"}
              </span>
              {platformConnectionStatus.authenticated && (
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                  Authenticated
                </span>
              )}
            </div>
            {platformConnectionStatus.userInfo && (
              <div className="text-sm text-muted-foreground">
                <p>User: {platformConnectionStatus.userInfo.email}</p>
                <p>Name: {platformConnectionStatus.userInfo.name}</p>
                <p>Role: {platformConnectionStatus.userInfo.role}</p>
              </div>
            )}
            {platformConnectionStatus.error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                Error: {platformConnectionStatus.error}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Last checked:{" "}
              {new Date(platformConnectionStatus.lastChecked).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Sync Configuration Section - Disabled if not connected */}
      <div
        className={`rounded-lg border p-4 bg-card ${!isConnected ? "opacity-60" : ""}`}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-md font-semibold">Platform Synchronization</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Sync clients and users from platform.byteable.ai
            </p>
          </div>
          <button
            onClick={() => setShowSyncDialog(true)}
            disabled={!isConnected}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Sync
          </button>
        </div>

        {!isConnected && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Connection Required:</strong> Please connect to the
              platform above before using sync features.
            </p>
          </div>
        )}

        {/* Sync Status */}
        <div className={!isConnected ? "pointer-events-none" : ""}>
          <SyncStatusIndicator key={syncStatusKey} onRefresh={() => {}} />
        </div>
      </div>

      {/* Sync History Section - Disabled if not connected */}
      <div
        className={`rounded-lg border p-4 bg-card ${!isConnected ? "opacity-60" : ""}`}
      >
        <h3 className="text-md font-semibold mb-4">Sync History</h3>
        <div className={!isConnected ? "pointer-events-none" : ""}>
          <SyncHistoryPanel limit={10} />
        </div>
      </div>

      {/* Sync Dialog */}
      <PlatformSyncDialog
        isOpen={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        onSyncComplete={handleSyncComplete}
      />
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
